import { useEffect, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { zodResolver } from '@hookform/resolvers/zod';
import { TabsContent } from '@radix-ui/react-tabs';
import { useForm } from 'react-hook-form';
import ErrorIcon from '@/components/customercenter-ui/svgs/notifications/error';
import SuccessIcon from '@/components/customercenter-ui/svgs/notifications/success';
import { useAccount } from '@/frontastic';
import { ErrorNotification, SuccessNotification } from '@/helpers/utils/notification';
import { formatMobileNumber, TranslateText } from '@/helpers/utils/siteUtils';
import { UpdateEmailPayload, UpdatePhonePayload } from '@/sdk/composable-commerce/types/payloads/AccountPayloads';
import { DEFAULT_SITE_LANG, EDIT_PHONE_NUMBER, EDIT_EMAIL, FORM_FIELD_SPECS } from '../../../../../constants/constant';
import {
  CUSTOMER_DETAILS_HEADER_KEYS,
  CUSTOMER_DETAILS_RECORD_KEYS,
  PAYLOAD_QUERY,
  RESPONSE_SUCCESS_MESSAGE,
} from '../../customer-detail/constants';
import { PageTexts } from '../../customer-detail/i18n';
import { managePhoneEmailSchema } from '../../customer-detail/schema';
import { ManagePhoneEmail, ManagePhoneEmailType } from '../../customer-detail/types';
import { updatePhoneNumber, updateEmailAddress } from '../../customer-detail/utils';
import { Button } from '../../ui/button';
import InputField from '../../ui/customInput/input';
import InputMaskField from '../../ui/customInputMask';
import { Table, TableColumnConfig } from '../../ui/customTable/Table';
import { Dialog, DialogClose, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import Loader from '../../ui/loader';
import { Tabs, TabsList, TabsTrigger } from '../../ui/tabs';
import { useToast } from '../../ui/use-toast';

const ManagePhoneEmailModal = ({
  openModal,
  setOpenModal,
  currentEditType,
  setCurrentEditType,
  accountData,
  setAccountData,
}: ManagePhoneEmailType) => {
  const {
    handleSubmit,
    register,
    formState: { errors, isDirty },
    reset,
  } = useForm({
    resolver: zodResolver(managePhoneEmailSchema),
    mode: 'all',
  });
  const params = useSearchParams();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const { updateEmail, updatePhone } = useAccount();
  const [phoneNumber, setPhoneNumber] = useState([]);
  const [emailAddresses, setEmailAddresses] = useState([]);

  const Texts = TranslateText(PageTexts, DEFAULT_SITE_LANG);

  useEffect(() => {
    const phoneNumbers = [] as any;
    const emailAddresses = [] as any;
    accountData?.forEach((items: any) => {
      if (items?.key === 'phoneNumber' && items?.value) {
        phoneNumbers.push(...items.value);
      } else if (items?.key === 'email' && items?.value) {
        emailAddresses.push(...items.value);
      }
    });

    const MappedNumbers: any =
      Array.isArray(phoneNumbers) && phoneNumbers.length > 0
        ? phoneNumbers.map((items: any) => ({
            id: items?.HeaderInfo?.phoneRecordId || '',
            phoneNumber: items?.PhoneInfo?.phoneNumber || '',
          }))
        : [{ id: '', phoneNumber: '' }];

    const MappedEmails: any =
      Array.isArray(emailAddresses) && emailAddresses.length > 0
        ? emailAddresses.map((items: any) => ({
            id: items?.HeaderInfo?.emailRecordId || '',
            emailAddress: items?.EmailInfo?.emailAddress || '',
          }))
        : [{ id: '', emailAddress: '' }];

    setEmailAddresses(MappedEmails);
    setPhoneNumber(MappedNumbers);
  }, [accountData]);

  const [showEditModal, setShowEditModal] = useState(false);

  const [selectedValue, setSelectedValue] = useState({ id: '', phoneNumber: '', email: '' });
  const phoneNumberHeaders: TableColumnConfig<any>[] = [
    {
      key: CUSTOMER_DETAILS_RECORD_KEYS.PHONE_NUMBER,
      header: CUSTOMER_DETAILS_HEADER_KEYS.PHONE,
      render: (data) => <div className="gbmask">{formatMobileNumber(data ?? '')}</div>,
    },
    {
      key: CUSTOMER_DETAILS_RECORD_KEYS.ACTION,
      header: '',
      render: (data, rowData) => (
        <button
          className="cursor-pointer text-blueLight underline"
          onClick={() => {
            setShowEditModal(true);
            setSelectedValue((prev: any) => ({ ...prev, id: rowData?.id, phoneNumber: rowData?.phoneNumber }));
          }}
        >
          {Texts.editButton}
        </button>
      ),
    },
  ];
  const emailHeader: TableColumnConfig<any>[] = [
    {
      key: CUSTOMER_DETAILS_RECORD_KEYS.EMAIL_ADDRESS,
      header: CUSTOMER_DETAILS_HEADER_KEYS.EMAIL_ADDRESS,
      render: (data) => <div className="gbmask">{data}</div>,
    },
    {
      key: CUSTOMER_DETAILS_RECORD_KEYS.ACTION,
      header: '',
      render: (data, rowData) => (
        <button
          className="cursor-pointer text-blueLight underline"
          onClick={() => {
            setShowEditModal(true);
            setSelectedValue((prev: any) => ({ ...prev, id: rowData?.id, email: rowData?.emailAddress }));
          }}
        >
          {Texts.editButton}
        </button>
      ),
    },
  ];

  const handleEdit = async (data: ManagePhoneEmail) => {
    if (currentEditType === EDIT_PHONE_NUMBER && (!data?.phone || data?.phone.toString().trim() === '')) {
      ErrorNotification(toast, {
        description: 'Phone number cannot be blank',
        action: <ErrorIcon className="h-5 w-5" />,
      });
      return;
    }

    if (currentEditType === EDIT_EMAIL && !data?.email?.trim()) {
      ErrorNotification(toast, { description: 'Email cannot be blank', action: <ErrorIcon className="h-5 w-5" /> });
      return;
    }

    setShowEditModal(false);
    setLoading(true);

    if (currentEditType === EDIT_EMAIL) {
      const updatedData = updateEmailAddress(selectedValue?.id, selectedValue?.email, accountData);
      setAccountData(updatedData);
      const payload = {
        query: {
          customerId: params.get(PAYLOAD_QUERY.CUSTOMER_ID),
          divisionCode: params.get(PAYLOAD_QUERY.DIVISION_CODE),
        },
        data: {
          HeaderInfo: {
            isPrimary: true,
          },
          EmailInfo: {
            emailAddress: data?.email?.toString(),
          },
        },
      } as UpdateEmailPayload;

      await updateEmail(payload).then((res) => {
        setLoading(false);
        if (res?.code === RESPONSE_SUCCESS_MESSAGE) {
          SuccessNotification(toast, { description: 'Email Updated', action: <SuccessIcon className="h-5 w-5" /> });
          window.location.reload();
        } else {
          ErrorNotification(toast, { description: 'Error Updating Email', action: <ErrorIcon className="h-5 w-5" /> });
        }
      });
    } else {
      const updatedData = updatePhoneNumber(selectedValue?.id, selectedValue?.phoneNumber, accountData);
      setAccountData(updatedData);
      const payload = {
        query: {
          customerId: params.get(PAYLOAD_QUERY.CUSTOMER_ID),
          divisionCode: params.get(PAYLOAD_QUERY.DIVISION_CODE),
        },
        data: {
          HeaderInfo: {
            isPrimary: true,
          },
          PhoneInfo: {
            phoneNumber: data?.phone?.toString(),
          },
        },
      } as UpdatePhonePayload;

      await updatePhone(payload).then((res) => {
        setLoading(false);
        if (res.code === 'OK') {
          SuccessNotification(toast, {
            description: 'Phone Number Updated',
            action: <SuccessIcon className="h-5 w-5" />,
          });
          window.location.reload();
        } else {
          ErrorNotification(toast, {
            description: 'Error Updating Phone Number',
            action: <ErrorIcon className="h-5 w-5" />,
          });
        }
      });
    }
  };
  const onSubmit = (data: ManagePhoneEmail) => {
    handleEdit(data);
    reset();
  };
  return (
    <>
      {!showEditModal ? (
        <Dialog open={openModal} onOpenChange={(value) => setOpenModal(value)}>
          <DialogContent className="max-w-[560px] bg-white sm:rounded-md">
            {loading && <Loader />}
            <DialogHeader>
              {/* chng-text-25px */}
              <DialogTitle className="text-2xl font-bold">{Texts.manageEmailPhoneButton}</DialogTitle>
            </DialogHeader>
            <Tabs
              defaultValue={EDIT_EMAIL}
              onValueChange={(value) => setCurrentEditType(value)}
              value={currentEditType}
            >
              <section className="mb-4 pt-3">
                <TabsList>
                  <TabsTrigger value={EDIT_EMAIL} className="mr-5 px-0 pb-1 text-lg font-normal text-[#656668]">
                    {Texts.tabEmailButton}
                  </TabsTrigger>
                  <TabsTrigger value={EDIT_PHONE_NUMBER} className="px-0 pb-1 text-lg font-normal text-[#656668]">
                    {Texts.tabPhoneButton}
                  </TabsTrigger>
                </TabsList>
                <section className="mt-10">
                  <TabsContent value={EDIT_EMAIL}>
                    <Table columns={emailHeader} data={emailAddresses} />
                  </TabsContent>
                  <TabsContent value={EDIT_PHONE_NUMBER}>
                    <Table columns={phoneNumberHeaders} data={phoneNumber} />
                  </TabsContent>
                </section>
              </section>
            </Tabs>
            <section className="flex justify-start">
              <DialogClose className="underline">{Texts.cancelButton}</DialogClose>
            </section>
          </DialogContent>
        </Dialog>
      ) : (
        <Dialog
          open={showEditModal}
          onOpenChange={(value) => {
            if (!value) {
              setShowEditModal(false);
            }
          }}
        >
          <DialogContent className="bg-white sm:rounded-md">
            <DialogHeader>
              {/* chng-text-25px */}
              <DialogTitle className="text-2xl font-bold">
                {Texts.editButton}{' '}
                {currentEditType === EDIT_PHONE_NUMBER ? `${Texts.tabPhoneNumber}` : `${Texts.tabEmailButton}`}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit(onSubmit)}>
              {currentEditType === EDIT_PHONE_NUMBER ? (
                <InputMaskField
                  mask="999-999-9999"
                  items={`${Texts?.tabPhoneNumber}`}
                  id={FORM_FIELD_SPECS?.phone?.id}
                  htmlFor={FORM_FIELD_SPECS?.phone?.id}
                  register={register}
                  errors={errors}
                  label={Texts?.formPhoneNumberLabel}
                  classname="w-full"
                  value={selectedValue?.phoneNumber}
                  onChange={(e: React.ChangeEvent<any>) =>
                    setSelectedValue((prev: any) => ({ ...prev, phoneNumber: e.target.value }))
                  }
                  isNum
                />
              ) : (
                <InputField
                  items={Texts.tabEmailButton}
                  id={FORM_FIELD_SPECS.email.id}
                  htmlFor={FORM_FIELD_SPECS.emailAddress.id}
                  register={register}
                  value={selectedValue?.email}
                  onChange={(e: React.ChangeEvent<any>) =>
                    setSelectedValue((prev: any) => ({ ...prev, email: e.target.value }))
                  }
                  errors={errors}
                  label={Texts.formEmailAddressLabel}
                  classname="w-full"
                />
              )}

              <section className="mt-10 flex justify-start gap-4">
                <Button className="rounded-md bg-deepBlue p-4 text-white" disabled={!isDirty}>
                  {Texts.saveButton}
                </Button>
                <Button
                  className="p-0 underline"
                  onClick={() => {
                    reset();
                    setShowEditModal(false);
                  }}
                >
                  {Texts.cancelButton}
                </Button>
              </section>
            </form>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export default ManagePhoneEmailModal;
